import { createContext, useContext, useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import localforage from "localforage";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient, { request, ReqMethods } from "../lib/apiClient";
import { uploadFileInChunks } from "../lib/chunkedUpload";

const { GET, POST } = ReqMethods;

const UserContext = createContext();
UserContext.displayName = "UserContext";

function UserProvider({ children }) {
  const searchParams = new URLSearchParams(document.location.search);
  const isSupport = searchParams.get("isSupport");
  const navigate = useNavigate();

  const pathname = window.location.pathname;

  // Handle app-integrations route differently
  let encryptedData, decryptedData, userEmail, userLocationId;

  if (pathname.startsWith("/app-integrations/")) {
    // For app integration routes, get location ID from URL params
    const urlParams = new URLSearchParams(window.location.search);
    userLocationId = urlParams.get("state"); // state parameter contains location ID
    userEmail = null; // Not available in this context
  } else {
    // Original logic for other routes
    encryptedData = pathname.split("/")[1];
    if (encryptedData) {
      try {
        decryptedData = atob(encryptedData);
        userEmail = decryptedData.split(":")[0];
        userLocationId = decryptedData.split(":")[1];
      } catch (error) {
        console.error("Failed to decode user data from URL:", error);
        userEmail = null;
        userLocationId = null;
      }
    }
  }

  const [user, setUser] = useState({});
  // True once the initial localforage session load (updateUser) has run — lets the
  // router show a splash instead of flashing the sign-in screen on hard reload.
  const [authResolved, setAuthResolved] = useState(false);
  const [path, setPath] = useState(window.location.pathname + `${window.location.search}`);
  const [userActiveLocation, setUserActiveLocation] = useState();
  const [ActiveApps, setActiveApps] = useState();
  const [userLocations, setUserLocations] = useState([]);
  // True while GET /user/getchatuser (the call that resolves userLocations
  // on boot) is in flight, so the workspace-switcher dropdown can show a
  // loader instead of a stale/empty list on a hard refresh.
  const [isUserLocationsLoading, setIsUserLocationsLoading] = useState(false);
  // null until the backend has actually confirmed this session (via
  // getUser's /user/getchatuser on a resumed session, or checkLocation's
  // /chat/assistants/locationCheck on a fresh login) - user/authResolved
  // alone only mean "something was cached," not "the token is live."
  const [sessionVerified, setSessionVerified] = useState(null);
  const [checkIfGhlUser, setCheckIfGhlUser] = useState(false);
  const [agencyData, setAgencyData] = useState();
  const [isConnected, setIsConnected] = useState(false);
  const [GHLSessionKey, setGHLSessionKey] = useState("");
  const [accountActivePlan, setAccountActivePlan] = useState("");
  const [agentShow, setagentshow] = useState(false);
  const [accountPlugins, setaccountPlugins] = useState([]);
  const [isFreePlan, setIsFreePlan] = useState(false);
  const location = useLocation()

  // Uploads each file in small chunks (lib/chunkedUpload) instead of one big
  // multipart POST, so a large Knowledge Base doc or chat attachment doesn't
  // trip Cloud Run's request-size limit (413). Each branch below keeps the
  // exact response shape its existing callers already read
  // (response.data.success / response.data.data / response.data.crashfiles)
  // — only the transport underneath changed.
  const contextuploadFilefrominput = async (
    files,
    profileId,
    userName,
    assistant_ids,
    compType
  ) => {
    const audiofilestype = [
      "audio/mpeg", // mp3
      "video/mp4", // mp4
      "video/mpeg", // mpeg
      "audio/mp4", // m4a
      "audio/wav", // wav
    ];
    const audiofiles = [];
    const otherFiles = [];
    const assistants = assistant_ids?.map((item) => item.value) || [];

    for (let index = 0; index < files.length; index++) {
      const fileType = files[index].file
        ? files[index].file.type
        : files[index].type;

      if (audiofilestype.includes(fileType)) {
        audiofiles.push(files[index]);
      } else {
        otherFiles.push(files[index]);
      }
    }

    // Uploads every file in the list with the given backend compType,
    // flattening each file's per-assistant/per-registration results into one
    // array and collecting the names of any files that failed outright.
    const uploadAll = async (fileList, uploadCompType) => {
      const results = [];
      const crashfiles = [];
      for (const entry of fileList) {
        const rawFile = entry.file ? entry.file : entry;
        try {
          const response = await uploadFileInChunks(rawFile, {
            profileId,
            username: userName,
            assistantIds: assistants,
            compType: uploadCompType,
          });
          if (response?.success) {
            results.push(...(response.data?.results || []));
          } else {
            crashfiles.push(rawFile.name);
          }
        } catch (error) {
          console.log(error);
          crashfiles.push(rawFile.name);
        }
      }
      return { results, crashfiles };
    };

    if (compType === "shared_knowledge") {
      const { results, crashfiles } = await uploadAll([...audiofiles, ...otherFiles], "shared_knowledge");
      return {
        data: {
          success: results.length > 0,
          data: results,
          crashfiles,
        },
      };
    }

    // Audio only gets siloed into the live chat-audio flow (registers it
    // against a chat assistant via the external /api/chat/audio endpoint)
    // when the caller didn't ask for something else explicitly. An explicit
    // "knowledge_base"/"data_interpreter" request (e.g. FileTooBigModal's
    // "Add to Knowledge Base") must upload audio the same way as any other
    // file — routing an .m4a there into chat_audio instead was wrong and
    // crashed with a 500.
    const explicitCompType = compType === "knowledge_base" || compType === "data_interpreter";

    // Matches the pre-existing quirk this replaced: if there are audio files,
    // only they get uploaded/returned — otherFiles below never ran in that case.
    if (audiofiles.length > 0 && !explicitCompType) {
      const { results, crashfiles } = await uploadAll(audiofiles, "chat_audio");
      return {
        data: {
          success: results.length > 0,
          data: results,
          crashfiles,
        },
      };
    }

    const filesForGenericUpload = explicitCompType ? [...audiofiles, ...otherFiles] : otherFiles;
    if (filesForGenericUpload.length > 0) {
      const { results, crashfiles } = await uploadAll(
        filesForGenericUpload,
        compType === "data_interpreter" ? "data_interpreter" : "knowledge_base"
      );
      return {
        data: {
          success: results.length > 0,
          data: results,
          crashfiles,
        },
      };
    }
  };


  const contextUploadFilesAsync = async (
    files,
    profileId,
    userName,
    assistant_ids,
    userId,
    compType,
  ) => {
    const formData = new FormData();
    const assistants = assistant_ids?.map((item) => item.value);
    for (const f of files) {
      formData.append("file", f.file ? f.file : f);
    }
    formData.append("profile_id", profileId);
    formData.append("username", userName || "");
    formData.append("user_id", userId || user?._id || "");
    formData.append("assistant_ids", JSON.stringify(assistants || []));
    formData.append("model_name", "openai/gpt-4o");
    formData.append("compType", compType || "knowledge_base");
    formData.append("isStreaming", true);

    try {
      const response = await apiClient.post(
        `/chat/assistants/files/upload-async`,
        formData,
        { "Content-Type": "multipart/form-data" },
      );
      return response?.data;
    } catch (error) {
      return error?.response?.data || { success: false };
    }
  };

  const Update = async (updates) => {
    const newUser = { ...user, ...updates };
    setUser(newUser);
    await localforage.setItem("user", newUser);
  };

  const clear = async () => {
    try {
      await localforage.clear();
    } catch (err) {
      console.error("Error clearing localforage on logout", err);
    }
    setUser({});
    setUserActiveLocation({});
    setPath('');

    window.location.replace("/auth/sign-in");
  };

  const saveActiveLocation = async (activeLocation) => {
    await localforage.setItem("active_location", activeLocation);
    setUserActiveLocation(activeLocation);
  };

  const clearAllData = async () => {
    const sessionKeyValue = await localforage.getItem("sessionKey");
    // alert(sessionKeyValue)
    await localforage
      .clear()
      .then(() => {
        localforage.setItem("sessionKey", sessionKeyValue);
      })
      .catch((err) => {
        console.error("Error clearing localforage data", err);
      });
  };

  const isValidLocationId = (locationId) => {
    const locationIdRegex = /^[A-Za-z0-9]{20}$/;
    return locationIdRegex.test(locationId);
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const checkLocation = async (
    userLocationId,
    userEmail,
    userName,
    redirectType = "GHL"
  ) => {
    const payload = { location_id: userLocationId };
    try {
      const response = await request(POST, "/chat/assistants/locationCheck", {
        payload,
      });
      // Reaching here without a thrown error means the backend accepted this
      // session's token - the strongest "really logged in" signal the fresh
      // sign-in flow (single- or multi-location) has.
      setSessionVerified(true);

      if (response?.data?.data.length > 0) {
        setIsConnected(true);
        check_if_account_have_permissionto_create_agent();
      } else {
        // setTimeout(() => {
        //   window.location.href = `/onboarding?profile_id=${userLocationId}&profile_name=${userName}&username=${userName}&type=${redirectType}&email=${userEmail}`;
        //   check_if_account_have_permissionto_create_agent();
        // }, [2000])
      }
    } catch (error) {
      setSessionVerified(false);
      // setTimeout(() => {
      //   window.location.href = `/onboarding?profile_id=${userLocationId}&profile_name=${userName}&username=${userName}&type=${redirectType}&email=${userEmail}`;
      //   check_if_account_have_permissionto_create_agent();
      // }, [4000])
    }
  };

  useEffect(() => {
    window.addEventListener('loadstart', () => {
      setPath(location.pathname + location.search)
    })
  }, [location])

  useEffect(() => {
    if (encryptedData !== 'auth' && encryptedData !== 'app') {
      refreshpage()
    }
  }, [encryptedData, decryptedData, userEmail])

  const value = useMemo(
    () => [
      user,
      Update,
      clear,
      setUserActiveLocation,
      userActiveLocation,
      saveActiveLocation,
      isValidEmail,
      isValidLocationId,
      userLocations,
      checkIfGhlUser,
      setUserLocations,
      agencyData,
      ActiveApps,
      isConnected,
      GHLSessionKey,
      checkLocation,
      contextuploadFilefrominput,
      accountActivePlan,
      agentShow,
      accountPlugins,
      path,
      authResolved,
      contextUploadFilesAsync,
      isUserLocationsLoading,
      sessionVerified,
    ],
    [
      user,
      Update,
      clear,
      setUserActiveLocation,
      userActiveLocation,
      saveActiveLocation,
      isValidEmail,
      isValidLocationId,
      userLocations,
      checkIfGhlUser,
      setUserLocations,
      agencyData,
      ActiveApps,
      isConnected,
      GHLSessionKey,
      checkLocation,
      contextuploadFilefrominput,
      accountActivePlan,
      agentShow,
      accountPlugins,
      path,
      authResolved,
      contextUploadFilesAsync,
      isUserLocationsLoading,
      sessionVerified,
    ]
  );

  const getUser = async (localUser, activeLocation) => {
    const token = await localforage.getItem("token");
    if (!activeLocation?.id) {
      return
    }
    setIsUserLocationsLoading(true);
    let response;
    try {
      response = await request(GET, "/user/getchatuser", {
        query: `_id=${localUser?._id}&location_id=${activeLocation?.id}`,
        token,
      });
    } catch (error) {
      setIsUserLocationsLoading(false);
      setSessionVerified(false);
      return;
    }
    setIsUserLocationsLoading(false);
    // The server accepted the cached token - this resumed session is real,
    // not just leftover localforage data from a previous/expired login.
    setSessionVerified(true);
    if (response) {
      const updatedUser = { ...response.data, token };
      setUser({ ...localUser, ...updatedUser });
      await localforage.setItem("user", { ...localUser, ...updatedUser });
      // setUserActiveLocation(response.activeLocations[0]);
      setUserLocations(response.activeLocations);

      // A workspace switch persists a sibling location object, which
      // getchatuser returns without agenct_config.plugins (only the matched
      // active location carries it). The refetch above returns the switched-to
      // location in full, so heal the persisted copy when — and only when —
      // it is missing plugins the server has; unconditional updates would
      // retrigger location-keyed effects on every boot.
      const freshActive = response.activeLocations?.find(
        (location) => location.id === activeLocation.id
      );
      const missingPlugins =
        freshActive?.agenct_config?.plugins &&
        !activeLocation?.agenct_config?.plugins;
  
      // plan_gates drives the Plans/Wallet rows in the Configure nav, and the
      // menu prefers the persisted copy over the (slower) SaaS-products list.
      // So refresh on any DIFFERENCE, not just on absence: after a plan change
      // the persisted copy still has plan_gates, and a "missing only" check
      // would keep serving the previous plan's rows forever.
      const staleGates = activeLocation?.plan_gates;
      const nextGates = freshActive?.plan_gates;
      const planGatesChanged =
        nextGates &&
        (!staleGates ||
          staleGates.isenable_account !== nextGates.isenable_account ||
          staleGates.developer_plan !== nextGates.developer_plan ||
          staleGates.rebilling_ai_active !== nextGates.rebilling_ai_active);

      if (missingPlugins) {
        setUserActiveLocation(freshActive);
        await localforage.setItem("active_location", freshActive);
      } else if (planGatesChanged) {
        // Graft the one field rather than swapping wholesale: freshActive can
        // be a sibling copy stripped of the plugins blob, and replacing would
        // throw away plugins the local copy already has.
        const healed = { ...activeLocation, plan_gates: nextGates };
        setUserActiveLocation(healed);
        await localforage.setItem("active_location", healed);
      }

      window.maybe_v2_usr = {
        usr_id: updatedUser?.userId,
        usr_name: updatedUser?.userName,
        usr_email: updatedUser?.email,
      };
    }
  };

  const updateUser = async () => {
    const localUser = await localforage.getItem("user");
    const activeLocation = await localforage.getItem("active_location");
    const agencyInfo = await localforage.getItem("agency");
    const token = await localforage.getItem("token");
    setUser({ ...localUser, ...(token ? { token } : {}) });
    setUserActiveLocation(activeLocation);
    setAgencyData(agencyInfo);
    getUser(localUser, activeLocation)
    setAuthResolved(true);
  };

  const refreshpage = async () => {
    const localUser = await localforage.getItem("user");
    if (!localUser) {
      const payload = {
        email: userEmail,
        location_id: userLocationId,
        ghlUser: true,
        role: "agency",
      };
      let response;
      try {
        response = await request(POST, "/auth/app/login", { payload });
      } catch (error) {
        response = null;
      }
      if (response) {
        const { token, ...userData } = response.data;
        await localforage.setItem("token", token);
        await localforage.setItem("user", { ...userData });
        await localforage.setItem("agency", response.agency);
        await localforage.setItem('active_location', response?.activeLocations[0])
        updateUser();
      }
    }
  };

  const sessionstore = async () => {
    try {
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      const sessionKey = url.searchParams.get("sessionKey");

      if (sessionKey) {
        await localforage.setItem("sessionKey", sessionKey);
        setGHLSessionKey(sessionKey);
      } else {
        const storedSessionKey = await localforage.getItem("sessionKey");
        if (storedSessionKey) {
          setGHLSessionKey(storedSessionKey);
        }
      }
    } catch (error) {
      console.error("Failed to store session key:", error);
    }
  };

  // const checkSSOUser = async () => {
  //   const localUser = await localforage.getItem('user')
  //   // clearAllData();
  //   const payload = {
  //     email: userEmail,
  //     location_id: userLocationId,
  //     ghlUser: true,
  //     role: "agency",
  //   };
  //   const { response } = await Service.auth.login({ payload });
  //   if (response) {
  //     const { token, ...userData } = response.data;
  //     await localforage.setItem("token", token);
  //     await localforage.setItem("user", { ...localUser, ...userData });
  //     if (response.brand) await localforage.setItem("brand", response.brand);
  //     if (response.agency) {
  //       await localforage.setItem("agency", response.agency);
  //       setAgencyData(response.agency);
  //     }
  //     updateUser(response.data);
  //     saveActiveLocation(response.activeLocations[0]);
  //     setUserLocations(response.activeLocations);
  //     sessionstore();
  //     const redirectUrl = `/${btoa(
  //       `${userEmail}:${userLocationId}`
  //     )}/app/chatapp${isSupport === "true" ? "?isSupport=true" : ""}`;
  //     navigate(redirectUrl);
  //   } else {
  //     const redirectUrl = `/auth/sign-in`;
  //     navigate(redirectUrl);
  //   }
  // };

  const check_if_account_have_permissionto_create_agent = async () => {
    const activeLocation = await localforage.getItem("active_location");

    const response = await request(GET, "/accounts/getbyid", {
      query: `id=${activeLocation?.id}`,
    });
    if (response.data?.agenct_config?.allow) {
      setagentshow(true);
      setaccountPlugins(response.data?.agenct_config?.plugins);
    } else {
      setagentshow(false);
      setaccountPlugins([]);
    }
  };
  useEffect(() => {
    updateUser()
  }, []);


  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

function useUserInfo() {
  return useContext(UserContext) || [];
}

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { UserProvider, useUserInfo };
